// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — small panels that stand beside the globe in the headset, each
 * with its own content: a video, a picture or a few lines of text (the Khipu
 * plan, slice 4). Geo-media shows a radio or camera's picture and
 * its label through them (`vrGeoMedia`).
 *
 * A panel is placed by an offset from the globe's centre in metres (x to
 * the right, y up, z toward the room's front), the way the geo-media panel
 * has always stood, and turns to face the viewer every frame. Written from
 * scratch for AYNI from the geo-media panel's needs: nothing here comes
 * from TerraViz's headset overlay code.
 */
import type * as THREE from 'three'
import { createCanvasPanel, PANEL_COLORS, type CanvasPanel } from './vrPanelKit'
import { wrapText } from './vrKhipuPanel'

export interface WorldPanelPlace {
  /** From the globe's centre, in metres. */
  offset: { x: number; y: number; z: number }
  /** The plane, in metres. */
  size: { width: number; height: number }
}

export interface VrWorldPanelsHandle {
  readonly group: THREE.Group
  /** Show (or replace) panel `id` playing `video`. */
  showVideo(id: string, video: HTMLVideoElement, place: WorldPanelPlace): void
  /** Show (or replace) panel `id` with a decoded picture. */
  showImage(id: string, image: HTMLImageElement, place: WorldPanelPlace): void
  /** Show (or replace) panel `id` with lines of text, framed. */
  showText(id: string, text: string, place: WorldPanelPlace): void
  hide(id: string): void
  has(id: string): boolean
  /** Stand every panel at its offset from the globe (times the globe's scale, so a grown globe never swallows it), facing the viewer. */
  update(globeCentre: THREE.Vector3, viewer: THREE.Vector3, scale?: number): void
  dispose(): void
}

interface Shown {
  mesh: THREE.Mesh
  place: WorldPanelPlace
  dispose(): void
}

/** Text canvas pixels per metre of panel. */
const TEXT_PX_PER_M = 1400

export function createVrWorldPanels(THREE_: typeof THREE): VrWorldPanelsHandle {
  const group = new THREE_.Group()
  const shown = new Map<string, Shown>()
  const scratch = new THREE_.Vector3()

  function put(id: string, item: Shown): void {
    hide(id)
    item.mesh.renderOrder = 10
    group.add(item.mesh)
    shown.set(id, item)
  }

  function hide(id: string): void {
    const item = shown.get(id)
    if (!item) return
    group.remove(item.mesh)
    item.dispose()
    shown.delete(id)
  }

  function textured(texture: THREE.Texture, place: WorldPanelPlace): Shown {
    texture.colorSpace = THREE_.SRGBColorSpace
    const material = new THREE_.MeshBasicMaterial({ map: texture, transparent: true, depthTest: false, depthWrite: false, side: THREE_.DoubleSide })
    const geometry = new THREE_.PlaneGeometry(place.size.width, place.size.height)
    const mesh = new THREE_.Mesh(geometry, material)
    return {
      mesh,
      place,
      dispose: () => {
        texture.dispose()
        material.dispose()
        geometry.dispose()
      },
    }
  }

  return {
    group,
    showVideo(id, video, place) {
      put(id, textured(new THREE_.VideoTexture(video), place))
    },
    showImage(id, image, place) {
      const texture = new THREE_.Texture(image)
      texture.needsUpdate = true
      put(id, textured(texture, place))
    },
    showText(id, text, place) {
      const canvasW = Math.round(place.size.width * TEXT_PX_PER_M)
      const canvasH = Math.round(place.size.height * TEXT_PX_PER_M)
      const panel: CanvasPanel<never> = createCanvasPanel(THREE_, { widthM: place.size.width, heightM: place.size.height, canvasW, canvasH })
      panel.draw((p) => {
        p.frame(24)
        const pad = 28
        p.g.font = p.font(30)
        const lines = text.split('\n').flatMap((l) => wrapText((s) => p.g.measureText(s).width, l, canvasW - pad * 2))
        const room = Math.max(1, Math.floor((canvasH - pad * 2) / 40))
        lines.slice(0, room).forEach((line, i) => {
          p.text(line, pad, pad + 20 + i * 40, { size: 30, weight: i === 1 ? 600 : 500, color: i === 0 ? PANEL_COLORS.accentText : PANEL_COLORS.text, maxWidth: canvasW - pad * 2 })
        })
      })
      put(id, { mesh: panel.mesh, place, dispose: () => panel.dispose() })
    },
    hide,
    has: (id) => shown.has(id),
    update(globeCentre, viewer, scale = 1) {
      const k = Math.max(1, scale)
      for (const item of shown.values()) {
        scratch.set(globeCentre.x + item.place.offset.x * k, globeCentre.y + item.place.offset.y * k, globeCentre.z + item.place.offset.z * k)
        item.mesh.position.copy(scratch)
        item.mesh.lookAt(viewer)
      }
    },
    dispose() {
      for (const id of [...shown.keys()]) hide(id)
    },
  }
}
